import React, { useState } from "react";
import { addDays } from "date-fns";
import {
  DropdownMultiSelect,
  PillSelect,
  DateRangeInput,
  DateInput,
  TokenizedTextInput,
} from "@/components/FormInputs";
import { Card } from "@/components/ui";
import { studentInterests } from "@/__mocks__/studentInterestsData";
import { counselors } from "@/__mocks__/counselorsMockData";
import { useTranslation } from "react-i18next";
import { DateRange } from "react-day-picker";

const generateOptions = (values: string[]) =>
  values.map((value) => ({
    label: value,
    value: value.toLowerCase().trim().replace(/\s+/g, "-"),
  }));

const counselorOptions = generateOptions(counselors);
const interestOptions = generateOptions(studentInterests);

export const FormInputElements = () => {
  const [multiselectSearchValue, setDropdownMultiSelectSearchValue] = useState<
    string[]
  >([]);
  const [value, setValue] = useState<string[]>([]);
  const [pillSelectValue, setPillSelectValue] = useState<string[]>([]);

  const [date, setDate] = useState<Date | undefined>();
  const [range, setRange] = useState<DateRange | undefined>({
    from: new Date(2025, 4, 4),
    to: addDays(new Date(2025, 4, 4), 20),
  });
  const { t } = useTranslation("translation", {
    keyPrefix: "components.formInputs",
  });

  const [zipCodes, setZipCodes] = useState<string[]>(["90029", "234232"]);
  const [tokens, setTokens] = useState<string[]>([]);
  const getInputs = () => [
    <>
      <h3 className="mb-2 font-bold">{t("dpMultiWithSearch")}</h3>
      <DropdownMultiSelect
        placeholder={t("selectInterest")}
        allowSearch={true}
        emptyResults={t("emptyResultsInterests")}
        value={multiselectSearchValue}
        onUpdate={setDropdownMultiSelectSearchValue}
        options={interestOptions}
      />
    </>,
    <>
      <h3 className="mb-2 font-bold">{t("dpMultiWithSearch")}</h3>
      <DropdownMultiSelect
        placeholder={t("selectCounselor")}
        emptyResults={t("emptyResultsCounselors")}
        value={value}
        onUpdate={setValue}
        options={counselorOptions}
      />
    </>,
    <>
      <h3 className="mb-2 font-bold">{t("pillsMultiSelect")}</h3>
      <PillSelect
        allowMultiple={true}
        value={pillSelectValue}
        onUpdate={setPillSelectValue}
        options={[
          { label: "Inquired", value: "Inquired" },
          { label: "Applied", value: "Applied" },
          { label: "Admitted", value: "Admitted" },
          { label: "Committed", value: "Committed" },
          { label: "Enrolled", value: "Enrolled" },
        ]}
      />
    </>,
    <>
      <h3 className="mb-2 font-bold">{t("dateSelector")}</h3>
      <DateInput
        value={date}
        onUpdate={(item) => {
          setDate(item);
        }}
        placeholder={t("datePlaceholder")}
      />
    </>,
    <>
      <h3 className="mb-2 font-bold">{t("dateRangeSelector")}</h3>
      <DateRangeInput
        value={range}
        onUpdate={setRange}
        placeholder={t("datePlaceholder")}
      />
    </>,
    <>
      <div>
        <h3 className="font-bold">{t("tokenGeneric")}</h3>
        <div className="mb-3 text-xs">{t("tokenGenericSub")}</div>
      </div>
      <TokenizedTextInput
        onUpdate={setTokens}
        value={tokens}
        placeholder={t("tokenPlaceholder")}
      />
    </>,
    <>
      <div>
        <h3 className="font-bold">{t("zipCode")}</h3>
        <div className="mb-3 text-xs">{t("zipCodeSub")}</div>
      </div>
      <TokenizedTextInput
        onUpdate={setZipCodes}
        value={zipCodes}
        placeholder={t("zipPlaceholder")}
        inputFilter={(str) => {
          return str.replace(/[^0-9\s,]/, "");
        }}
        tokenIsValid={(str) => {
          return /^[1-9][0-9]{4}$/.test(str);
        }}
      />
    </>,
  ];

  return (
    <div className="my-3">
      <h2 className="mb-3 text-lg font-bold">{t("title")}</h2>
      <div className="inline-grid grid-cols-1 gap-3 md:grid-cols-2">
        {getInputs().map((input, idx) => (
          <Card key={idx} className="max-w-md gap-3 px-5 pt-3 pb-4">
            {input}
          </Card>
        ))}
      </div>
    </div>
  );
};
